import { Button } from "@dash/form";
import { BaseEntry, CommentEntry, EntriesList } from "@dash/history";
import { SectionHeader } from "@dash/page";
import { useProfiles } from "@megarax/features";
import { isToday, isYesterday } from "date-fns";
import React, { useState } from "react";
import { MdOutlineInsertDriveFile, MdOutlineMailOutline, MdSwapHoriz, MdOutlineDownload } from "react-icons/md";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { MessageDto, TicketDetailDto, TicketHistoryEntry, ticketStatusLabels } from "@megaron/support-contracts";

import { toDisplayName } from "../shared/displayName";
import { updateTicketPayload } from "../ticket/supportUpdateTicketPayload";
import { getSupportTicketMessagesSectionTitle } from "./supportTicketMessagesSectionTitle";
import { SupportTicketCommentAttachments } from "./SupportTicketCommentAttachments";
import { SupportTicketMessageTimeline } from "./SupportTicketMessageTimeline";
import { SupportTicketNoteComposer } from "./SupportTicketNoteComposer";
import type { NoteAttachmentEntry } from "./supportTicketNoteAttachmentTypes";
import { SupportTicketPrintView } from "./SupportTicketPrintView";
import { SupportTicketReplyComposer } from "./SupportTicketReplyComposer";
import { useSupportTicketPdf } from "./useSupportTicketPdf";

type TicketComment = TicketDetailDto["notes"][number];
type TicketCommentMention = TicketComment["mentions"][number];

type Props = { ticket: TicketDetailDto };

const historyStatusLabels: Record<string, string> = {
  ...ticketStatusLabels,
  inProgress: ticketStatusLabels.new,
  pending: ticketStatusLabels.open,
};

const formatDate = (date: Date): string => {
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const timeStr = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (isToday(date)) return `Dziś o ${timeStr}`;
  if (isYesterday(date)) return `Wczoraj o ${timeStr}`;
  return `${date.toLocaleDateString("pl-PL")} ${timeStr}`;
};

const historyEntryTitle = (entry: TicketHistoryEntry): string => {
  if (entry.entryType === "ticketCreated") return "Utworzono zgłoszenie";
  const from = historyStatusLabels[entry.oldValue ?? ""] ?? entry.oldValue ?? "";
  const to = historyStatusLabels[entry.newValue ?? ""] ?? entry.newValue ?? "";
  return `Zmiana statusu: ${from} → ${to}`;
};

type MessageTimelineItem = { kind: "message"; data: MessageDto; date: Date };
type NoteTimelineItem = { kind: "note"; data: TicketComment; date: Date };
type HistoryTimelineItem = { kind: "history"; data: TicketHistoryEntry; date: Date };
type HistoryItem = NoteTimelineItem | HistoryTimelineItem;

const buildMessageTimeline = (ticket: TicketDetailDto): MessageTimelineItem[] =>
  ticket.messages
    .map((m) => ({ kind: "message" as const, data: m, date: new Date(m.createdAt) }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

const buildHistoryTimeline = (ticket: TicketDetailDto): HistoryItem[] => {
  const notes: HistoryItem[] = (ticket.notes ?? []).map((n) => ({
    kind: "note" as const,
    data: n,
    date: new Date(n.createdAt),
  }));
  const hasCreatedEntry = (ticket.history ?? []).some((h) => h.entryType === "ticketCreated");
  const syntheticCreated: HistoryItem[] = hasCreatedEntry
    ? []
    : [
        {
          kind: "history" as const,
          data: {
            id: ticket.id,
            entryType: "ticketCreated" as const,
            changedByEmail: null,
            oldValue: null,
            newValue: null,
            createdAt: ticket.createdAt,
          },
          date: new Date(ticket.createdAt),
        },
      ];
  const history: HistoryItem[] = (ticket.history ?? []).map((h) => ({
    kind: "history" as const,
    data: h,
    date: new Date(h.createdAt),
  }));
  return [...notes, ...history, ...syntheticCreated].sort((a, b) => b.date.getTime() - a.date.getTime());
};

export const SupportTicketMessagesAndNotes: React.FC<Props> = ({ ticket }) => {
  const queryClient = useQueryClient();
  const { profile } = useProfiles();
  const { fetchAndPrint, isLoading: isPdfLoading, printRef, attachmentsMap } = useSupportTicketPdf(ticket);
  const createCommentMutation = useClientManager("comments").createComment().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });
  const updateTicketMutation = useClientManager("support").updateTicket().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });
  const [notesInput, setNotesInput] = useState("");
  const [noteMentions, setNoteMentions] = useState<TicketCommentMention[]>([]);
  const [noteAttachments, setNoteAttachments] = useState<NoteAttachmentEntry[]>([]);

  const canSendNote = notesInput.trim().length > 0 || noteAttachments.length > 0;

  const sendNote = () => {
    if (!canSendNote) return;
    createCommentMutation.mutate({
      resourceType: "supportTicket",
      resourceId: String(ticket.id),
      body: notesInput.trim(),
      mentions: noteMentions,
      attachments: noteAttachments,
    }, {
      onSuccess: () => {
        setNotesInput("");
        setNoteMentions([]);
        setNoteAttachments([]);
        // Dodanie notatki do zgłoszenia "Nowy" przenosi je do "Otwarty".
        if (ticket.status === "new") {
          updateTicketMutation.mutate(updateTicketPayload(ticket, { status: "open" }));
        }
      },
    });
  };

  const messageTimeline = buildMessageTimeline(ticket);
  const historyTimeline = buildHistoryTimeline(ticket);
  const messagesSectionTitle = getSupportTicketMessagesSectionTitle(ticket);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {messageTimeline.length > 0 && (
        <>
          <SupportTicketPrintView ref={printRef} ticket={ticket} attachmentsMap={attachmentsMap} />
          <SectionHeader
            isHr
            actions={
              <Button
                variant="outline"
                size="small"
                icon={<MdOutlineDownload />}
                onClick={() => { if (!isPdfLoading) void fetchAndPrint(); }}
                isLoading={isPdfLoading}
                isDisabled={isPdfLoading}
              >
                Pobierz PDF
              </Button>
            }
          >
            {messagesSectionTitle}
          </SectionHeader>
          <SupportTicketMessageTimeline
            ticket={ticket}
            messageTimeline={messageTimeline}
            formatDate={formatDate}
          />
        </>
      )}
      {ticket.threadId && <SupportTicketReplyComposer ticket={ticket} />}
      <SectionHeader isHr>Historia</SectionHeader>
      {historyTimeline.length > 0 && (
        <EntriesList>
          {historyTimeline.map((item) => {
            if (item.kind === "note") {
              const profileData = profile(item.data.authorEmail ?? null);
              const fullName = profileData
                ? `${profileData.firstName} ${profileData.lastName}`
                : toDisplayName(item.data.authorEmail?.split("@")[0] ?? "Notatka");
              return (
                <CommentEntry
                  key={item.data.id}
                  fullName={fullName}
                  time={formatDate(item.date)}
                  message={
                    <>
                      <div css={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.data.body}</div>
                      <SupportTicketCommentAttachments commentId={item.data.id} attachments={item.data.attachments ?? []} />
                    </>
                  }
                  imageSrc={profileData?.profilePictureUrl}
                />
              );
            }
            return (
              <BaseEntry
                key={item.data.id}
                title={historyEntryTitle(item.data)}
                icon={
                  item.data.entryType === "ticketCreated" ? (
                    <MdOutlineInsertDriveFile />
                  ) : item.data.entryType === "statusChanged" ? (
                    <MdSwapHoriz />
                  ) : (
                    <MdOutlineMailOutline />
                  )
                }
                time={formatDate(item.date)}
                additionalText={
                  item.data.changedByEmail ? toDisplayName(item.data.changedByEmail.split("@")[0]) : undefined
                }
              />
            );
          })}
        </EntriesList>
      )}
      <SupportTicketNoteComposer
        commentValue={notesInput}
        mentions={noteMentions}
        attachments={noteAttachments}
        onCommentChange={(comment, mentions) => {
          setNotesInput(comment);
          setNoteMentions(mentions);
        }}
        onAttachmentsChange={setNoteAttachments}
        onSend={sendNote}
        canSend={canSendNote}
        isLoading={"isLoading" in createCommentMutation ? createCommentMutation.isLoading : false}
      />
    </div>
  );
};
